import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {HttpErrorResponse} from '@angular/common/http';
import {DataService} from '../../../../Services/data.service';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {ConfirmDialogService} from '../../../../Services/confirm-dialog.service';

@Component({
  selector: 'app-account-import',
  templateUrl: './account-import.component.html',
  styleUrls: ['./account-import.component.css'],
  standalone: false
})
export class AccountImportComponent implements OnInit {

  vm: any = {
    // wizard step: 1=upload, 2=review, 3=done
    step: 1,
    selectedFile: null,
    fileName: '',
    mode: 'create',
    dryRun: false,
    includeMargins: false,
    uploading: false,
    importResult: null,
  };

  constructor(
    private router: Router,
    private titleService: Title,
    private tableDataService: DataService,
    private notification: BsToastService,
    private confirmDialog: ConfirmDialogService,
  ) {
  }

  ngOnInit(): void {
    this.titleService.setTitle('Chart of Accounts Import');
  }

  // ── File selection ─────────────────────────────────────────────────────────

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.vm.selectedFile = input.files[0];
      this.vm.fileName = input.files[0].name;
    }
  }

  get canUpload(): boolean {
    return !!this.vm.selectedFile;
  }

  // ── Upload / import ────────────────────────────────────────────────────────

  upload(): void {
    if (!this.canUpload) {
      return;
    }
    this.vm.uploading = true;
    const formData = new FormData();
    formData.append('file', this.vm.selectedFile);
    formData.append('mode', this.vm.mode);
    formData.append('dry_run', String(this.vm.dryRun));

    const isXlsx = this.vm.fileName.toLowerCase().endsWith('.xlsx');
    const endpoint = isXlsx
      ? `accounts/import/xlsx?mode=${this.vm.mode}&dry_run=${this.vm.dryRun}&include_margins=${this.vm.includeMargins}`
      : `accounts/import/csv?mode=${this.vm.mode}&dry_run=${this.vm.dryRun}`;

    this.tableDataService.postFormData(endpoint, formData).subscribe({
      next: (result: any) => {
        this.vm.uploading = false;
        this.vm.importResult = result;
        this.vm.step = result.saved || this.vm.dryRun ? 2 : 2;
      },
      error: (err: HttpErrorResponse) => {
        this.vm.uploading = false;
        if (err.status === 400 && err.error?.rows) {
          // Validation errors — show result table
          this.vm.importResult = err.error;
          this.vm.step = 2;
        } else if (err.status === 409) {
          this.notification.showErrorToast(err.error?.message || 'Replace mode blocked: existing GL activity detected.');
        } else {
          this.notification.showErrorToast(err.error?.message || 'Import failed. Please check the file and try again.');
        }
      }
    });
  }

  // ── Navigation ─────────────────────────────────────────────────────────────

  startOver(): void {
    this.vm.step = 1;
    this.vm.selectedFile = null;
    this.vm.fileName = '';
    this.vm.importResult = null;
    this.vm.dryRun = false;
    this.vm.mode = 'create';
    this.vm.includeMargins = false;
  }

  goToAccounts(): void {
    this.router.navigate(['/admin/account-list']).then();
  }

  // ── Export current accounts ────────────────────────────────────────────────

  exportCsv(): void {
    this.confirmDialog.confirmThis(
      ['Export Accounts as CSV', 'This will download the current chart of accounts as a CSV file. Continue?'],
      () => {
        this.tableDataService.getBlob('accounts/import/export.csv').subscribe({
          next: (blob: Blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'chart-of-accounts.csv';
            a.click();
            URL.revokeObjectURL(url);
          },
          error: () => this.notification.showErrorToast('Export failed. Please try again.')
        });
      },
      () => {
      }
    );
  }

  exportXlsx(): void {
    this.confirmDialog.confirmThis(
      ['Export Accounts as XLSX', 'This will download the current chart of accounts as an Excel file. Continue?'],
      () => {
        this.tableDataService.getBlob('accounts/import/export.xlsx').subscribe({
          next: (blob: Blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'chart-of-accounts.xlsx';
            a.click();
            URL.revokeObjectURL(url);
          },
          error: () => this.notification.showErrorToast('Export failed. Please try again.')
        });
      },
      () => {
      }
    );
  }

  // ── Download template ──────────────────────────────────────────────────────

  downloadTemplate(): void {
    const link = document.createElement('a');
    link.href = 'assets/templates/chart-of-accounts-import-template.csv';
    link.download = 'chart-of-accounts-import-template.csv';
    link.click();
  }

  // ── Result helpers ─────────────────────────────────────────────────────────

  get resultRows(): any[] {
    return this.vm.importResult?.rows || [];
  }

  get summary(): any {
    return this.vm.importResult?.summary || {};
  }

  get hasErrors(): boolean {
    return (this.summary.errors || 0) > 0;
  }

  rowBadgeClass(status: string): string {
    switch (status) {
      case 'created':
        return 'badge text-bg-success';
      case 'updated':
        return 'badge text-bg-primary';
      case 'skipped':
        return 'badge text-bg-secondary';
      case 'error':
        return 'badge text-bg-danger';
      default:
        return 'badge text-bg-light';
    }
  }

  rowClass(status: string): string {
    return '';
  }
}
